import { z } from 'zod';
import { esquemaConsultaPagina } from './comun.js';
/**
 * Contratos de administración.
 *
 * Lo que entra por los endpoints internos se acota acá, en un solo lugar, para
 * que el navegador y el servidor validen contra la MISMA definición. Dos reglas
 * gobiernan este archivo:
 *
 *  1. Todo esquema de escritura es `.strict()`. Un campo desconocido se
 *     rechaza con 400 en vez de ignorarse en silencio: un PATCH con un nombre
 *     de campo mal escrito respondiendo 200 deja al operador creyendo que
 *     guardó algo que no se guardó.
 *  2. El esquema NO decide permisos. Que `estado` sea solo de admin no se puede
 *     expresar en un tipo de Zod —depende de quién pide—, así que esa
 *     comprobación vive en el módulo y tiene su propio test. Acá solo se valida
 *     la forma y el rango.
 */
// ─── RUT chileno ────────────────────────────────────────────────────────────
/**
 * Formato con puntos y guion, tal como se escribe en una factura chilena.
 *
 * Se exige el formato canónico en vez de normalizar cualquier variante porque
 * el RUT es la clave natural del proveedor y del cliente: dos escrituras del
 * mismo número —`76086428-5` y `76.086.428-5`— crearían dos organizaciones
 * distintas para la misma empresa, que es justo lo que el índice único debería
 * impedir.
 */
export const FORMATO_RUT = /^\d{1,2}\.\d{3}\.\d{3}-[0-9kK]$/;
/**
 * Dígito verificador por módulo 11.
 *
 * El cuerpo se recorre de derecha a izquierda con la serie de multiplicadores
 * 2,3,4,5,6,7 que vuelve a 2 al agotarse. Resto 11 → '0'; resto 10 → 'K'.
 *
 * La v1 NO validaba el dígito: aceptaba cualquier cosa con la forma de un RUT,
 * así que un dígito tipeado al revés entraba a la base y no se detectaba hasta
 * que el SII rechazaba la factura, semanas después de la entrega.
 */
export function digitoVerificadorRut(cuerpo) {
    let suma = 0;
    let multiplicador = 2;
    for (let i = cuerpo.length - 1; i >= 0; i -= 1) {
        suma += Number(cuerpo[i]) * multiplicador;
        multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
    }
    const resto = 11 - (suma % 11);
    if (resto === 11)
        return '0';
    if (resto === 10)
        return 'K';
    return String(resto);
}
/** Formato canónico Y dígito verificador correcto. Las dos cosas. */
export function rutValido(rut) {
    if (!FORMATO_RUT.test(rut))
        return false;
    const guion = rut.lastIndexOf('-');
    const cuerpo = rut.slice(0, guion).replace(/\./g, '');
    const dv = rut.slice(guion + 1).toUpperCase();
    if (!/^\d+$/.test(cuerpo))
        return false;
    return digitoVerificadorRut(cuerpo) === dv;
}
export const esquemaRut = z
    .string({ required_error: 'Falta el RUT' })
    .trim()
    .max(20, 'El RUT es demasiado largo')
    .regex(FORMATO_RUT, 'El RUT debe tener el formato 76.086.428-5')
    .refine(rutValido, 'El dígito verificador del RUT no corresponde');
// ─── Enumeraciones ──────────────────────────────────────────────────────────
export const ESTADOS_PROVEEDOR = ['pendiente', 'aprobado', 'rechazado'];
/**
 * Los valores del DOMINIO, con tildes: 'Concepción', 'Valparaíso'.
 *
 * Prisma no admite tildes en el identificador de un enum, así que el esquema de
 * la base usa `Concepcion @map("Concepción")`. La traducción vive en
 * `servicios/configFlete.ts` y en ningún otro lugar.
 */
/**
 * Las reglas de selección de oferta, tal como viajan por la API.
 *
 * Se redeclaran acá en vez de importarse del dominio porque `contracts` no
 * depende de `domain`: es el paquete que ambos lados leen. El test
 * `apps/api/test/admin.test.ts` comprueba que las dos listas coincidan, así que
 * agregar una regla en el dominio y olvidarla acá no pasa desapercibido.
 */
export const REGLAS_SELECCION_DTO = [
    'mayor_costo',
    'menor_costo',
    'mayor_margen',
    'mayor_stock',
];
export const CIUDADES_ORIGEN = [
    'Santiago',
    'Antofagasta',
    'Calama',
    'Concepción',
    'Valparaíso',
];
export const ZONAS_FLETE = ['Norte Grande', 'Norte Chico', 'Centro', 'Sur'];
export const TIPOS_CLIENTE = ['minera', 'colaboradora'];
// ─── Campos comunes ─────────────────────────────────────────────────────────
const esquemaRazonSocial = z
    .string({ required_error: 'Falta la razón social' })
    .trim()
    .min(2, 'La razón social es demasiado corta')
    .max(200, 'La razón social es demasiado larga');
/** Contacto y correo son opcionales y pueden borrarse pasando `null`. */
const esquemaContacto = z.string().trim().max(160).nullish();
const esquemaEmailOpcional = z
    .string()
    .trim()
    .toLowerCase()
    .email('El correo no tiene un formato válido')
    .max(190)
    .nullish();
const esquemaDiasPago = z
    .number({ invalid_type_error: 'Los días de pago deben ser un número' })
    .int('Los días de pago deben ser un entero')
    .min(0, 'Los días de pago no pueden ser negativos')
    .max(365, 'Los días de pago no pueden superar un año');
// ─── Proveedores ────────────────────────────────────────────────────────────
export const esquemaConsultaProveedores = esquemaConsultaPagina.extend({
    estado: z.enum(ESTADOS_PROVEEDOR).optional(),
    q: z.string().trim().max(200).optional(),
});
export const esquemaCrearProveedor = z
    .object({
    razonSocial: esquemaRazonSocial,
    rut: esquemaRut,
    ciudadOrigen: z.enum(CIUDADES_ORIGEN, {
        required_error: 'Falta la ciudad de origen',
        invalid_type_error: 'La ciudad de origen no es válida',
    }),
    contacto: esquemaContacto,
    email: esquemaEmailOpcional,
    diasPago: esquemaDiasPago.optional(),
})
    .strict();
/**
 * Edición de proveedor.
 *
 * `estado` está en el esquema —tiene que poder viajar— pero el módulo lo rechaza
 * con 403 si quien pide no es admin: decide si los productos de ese proveedor se
 * publican, y eso mueve plata; no es del turno de un operador.
 */
export const esquemaEditarProveedor = z
    .object({
    razonSocial: esquemaRazonSocial.optional(),
    rut: esquemaRut.optional(),
    ciudadOrigen: z.enum(CIUDADES_ORIGEN).optional(),
    contacto: esquemaContacto,
    email: esquemaEmailOpcional,
    diasPago: esquemaDiasPago.optional(),
    estado: z.enum(ESTADOS_PROVEEDOR).optional(),
})
    .strict();
// ─── Clientes ───────────────────────────────────────────────────────────────
/**
 * Booleano de query string. `z.coerce.boolean()` no sirve: coacciona con la
 * veracidad de JavaScript y `?activo=false` —cadena no vacía— daría `true`,
 * que es el filtro contrario al que pidió la persona.
 */
const esquemaBooleanoConsulta = z.preprocess((v) => {
    if (typeof v !== 'string')
        return v;
    const s = v.trim().toLowerCase();
    if (s === '')
        return undefined;
    if (s === 'true' || s === '1' || s === 'si' || s === 'sí')
        return true;
    if (s === 'false' || s === '0' || s === 'no')
        return false;
    return v;
}, z.boolean().optional());
export const esquemaConsultaClientes = esquemaConsultaPagina.extend({
    activo: esquemaBooleanoConsulta,
    q: z.string().trim().max(200).optional(),
});
export const esquemaCrearCliente = z
    .object({
    razonSocial: esquemaRazonSocial,
    rut: esquemaRut,
    tipo: z.enum(TIPOS_CLIENTE, {
        required_error: 'Falta el tipo de cliente',
        invalid_type_error: 'El tipo de cliente no es válido',
    }),
    diasPago: esquemaDiasPago.optional(),
    contacto: esquemaContacto,
    email: esquemaEmailOpcional,
})
    .strict();
export const esquemaEditarCliente = z
    .object({
    razonSocial: esquemaRazonSocial.optional(),
    rut: esquemaRut.optional(),
    tipo: z.enum(TIPOS_CLIENTE).optional(),
    diasPago: esquemaDiasPago.optional(),
    contacto: esquemaContacto,
    email: esquemaEmailOpcional,
    activo: z.boolean().optional(),
})
    .strict();
// ─── Faenas ─────────────────────────────────────────────────────────────────
const esquemaNombreFaena = z
    .string({ required_error: 'Falta el nombre de la faena' })
    .trim()
    .min(2, 'El nombre de la faena es demasiado corto')
    .max(160, 'El nombre de la faena es demasiado largo');
export const esquemaConsultaFaenas = esquemaConsultaPagina.extend({
    clienteId: z.string().trim().length(36, 'El cliente no es válido').optional(),
});
export const esquemaCrearFaena = z
    .object({
    clienteId: z.string({ required_error: 'Falta el cliente' }).trim().min(1),
    nombre: esquemaNombreFaena,
    ciudad: z.string().trim().min(2, 'Falta la ciudad').max(120),
    region: z.string().trim().min(2, 'Falta la región').max(120),
    zonaFlete: z.enum(ZONAS_FLETE, {
        required_error: 'Falta la zona de flete',
        invalid_type_error: 'La zona de flete no es válida',
    }),
    activa: z.boolean().optional(),
})
    .strict();
/**
 * La faena NO cambia de cliente. Reasignarla movería órdenes históricas a otra
 * minera sin dejar rastro; si hace falta, se crea la faena en el otro cliente.
 */
export const esquemaEditarFaena = z
    .object({
    nombre: esquemaNombreFaena.optional(),
    ciudad: z.string().trim().min(2).max(120).optional(),
    region: z.string().trim().min(2).max(120).optional(),
    zonaFlete: z.enum(ZONAS_FLETE).optional(),
    activa: z.boolean().optional(),
})
    .strict();
// ─── Operadores logísticos ──────────────────────────────────────────────────
/**
 * El operador logístico: DÓNDE ENTREGAMOS la carga.
 *
 * En el recorrido real la carga va distribuidor → nuestra bodega → transporte
 * hasta el operador, que es el punto donde la dejamos y desde donde la minera
 * la retira hasta su faena. Elegirlo es decir a dónde va el despacho.
 *
 * POR AHORA NO TARIFICA. `zonaFlete` está en el modelo porque el operador es el
 * destino del tramo que sí hacemos, pero el flete se sigue calculando contra la
 * zona de la FAENA hasta que exista el tarifario del contrato. Mover eso ahora
 * cambiaría el precio de toda cotización nueva con números que nadie negoció.
 */
export const esquemaNombreOperador = z
    .string({ required_error: 'Falta el nombre del operador' })
    .trim()
    .min(2, 'El nombre del operador es demasiado corto')
    .max(160, 'El nombre del operador es demasiado largo');
export const esquemaCrearOperador = z
    .object({
    nombre: esquemaNombreOperador,
    rut: z.string().trim().min(1).max(20).optional(),
    contacto: z.string().trim().max(160).optional(),
    email: z.string().trim().email('El correo no es válido').max(190).optional(),
    telefono: z.string().trim().max(40).optional(),
    /** La dirección del andén donde se entrega, no la casa matriz. */
    direccion: z.string().trim().max(240).optional(),
    ciudad: z.string().trim().min(2, 'Falta la ciudad').max(120),
    region: z.string().trim().min(2, 'Falta la región').max(120),
    zonaFlete: z.enum(ZONAS_FLETE, {
        required_error: 'Falta la zona de flete',
        invalid_type_error: 'La zona de flete no es válida',
    }),
    activo: z.boolean().optional(),
})
    .strict();
export const esquemaEditarOperador = z
    .object({
    nombre: esquemaNombreOperador.optional(),
    rut: z.string().trim().min(1).max(20).nullish(),
    contacto: z.string().trim().max(160).nullish(),
    email: z.string().trim().email('El correo no es válido').max(190).nullish(),
    telefono: z.string().trim().max(40).nullish(),
    direccion: z.string().trim().max(240).nullish(),
    ciudad: z.string().trim().min(2).max(120).optional(),
    region: z.string().trim().min(2).max(120).optional(),
    zonaFlete: z.enum(ZONAS_FLETE).optional(),
    activo: z.boolean().optional(),
})
    .strict();
export const esquemaOperadorDto = z.object({
    id: z.string(),
    nombre: z.string(),
    rut: z.string().nullable(),
    contacto: z.string().nullable(),
    email: z.string().nullable(),
    telefono: z.string().nullable(),
    direccion: z.string().nullable(),
    ciudad: z.string(),
    region: z.string(),
    zonaFlete: z.enum(ZONAS_FLETE),
    activo: z.boolean(),
    /** Cuántos clientes lo tienen habilitado. Solo en el listado de administración. */
    clientes: z.number().int().optional(),
});
/**
 * Qué operadores puede elegir un cliente.
 *
 * La asignación es explícita y no «todos con todos»: el transporte a una faena
 * minera se negocia entre la minera y el transportista, y ofrecerle a un cliente
 * un operador con el que no tiene contrato es ofrecerle un despacho que no se va
 * a poder hacer.
 */
export const esquemaAsignarOperadores = z
    .object({
    operadorIds: z
        .array(z.string().trim().length(36, 'El operador no es válido'))
        .max(50, 'Demasiados operadores'),
    /**
     * El que se propone cuando el cliente no elige. Tiene que estar en la
     * lista: marcar como predeterminado uno que no está asignado dejaría al
     * cliente con un valor por defecto que no puede usar.
     */
    predeterminadoId: z.string().trim().length(36).nullish(),
})
    .strict();
/** Un operador tal como lo ve quien va a comprar. */
export const esquemaOperadorElegible = z.object({
    id: z.string(),
    nombre: z.string(),
    ciudad: z.string(),
    region: z.string(),
    direccion: z.string().nullable(),
    predeterminado: z.boolean(),
});
// ─── Categorías ─────────────────────────────────────────────────────────────
/**
 * Tope del margen de categoría.
 *
 * [0, 2] es un rango amplio a propósito —hasta 200 % sobre el costo— pero
 * ACOTADO: el margen multiplica el precio de todo lo que cuelga de la
 * categoría, y un dedo de más al teclear (`22` en vez de `0.22`) publicaría el
 * catálogo entero a veintitrés veces su precio. Un tope no evita el error de
 * criterio; evita el error de tipeo, que es el que de verdad ocurre.
 */
export const MARGEN_MINIMO = 0;
export const MARGEN_MAXIMO = 2;
export const esquemaEditarCategoria = z
    .object({
    nombre: z.string().trim().min(2).max(120).optional(),
    margenDefault: z
        .number({ invalid_type_error: 'El margen debe ser un número' })
        .min(MARGEN_MINIMO, 'El margen no puede ser negativo')
        .max(MARGEN_MAXIMO, 'El margen no puede superar 2, es decir 200 %')
        .optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, 'No hay nada que cambiar en la categoría');
// ─── Parámetros de negocio ──────────────────────────────────────────────────
/** Fracción de porcentaje: [0, 1). El 1 queda fuera —un IVA del 100 % no es un dato. */
const esquemaFraccionPct = (campo) => z
    .number({ invalid_type_error: campo + ' debe ser un número' })
    .min(0, campo + ' no puede ser negativo')
    .lt(1, campo + ' debe ser menor que 1: es una fracción, no un porcentaje');
/** Factor multiplicativo: estrictamente mayor que cero. */
const esquemaFactor = (campo) => z
    .number({ invalid_type_error: campo + ' debe ser un número' })
    .gt(0, campo + ' debe ser mayor que cero')
    .max(10, campo + ' está fuera de todo rango razonable');
/** Umbral entero estrictamente positivo: en cero divide por cero o deja un tramo vacío. */
const esquemaUmbral = (campo) => z
    .number({ invalid_type_error: campo + ' debe ser un número' })
    .int(campo + ' debe ser un entero')
    .gt(0, campo + ' debe ser mayor que cero');
/** Monto entero en CLP que puede valer cero: el cero desactiva la regla. */
const esquemaMontoOpcional = (campo) => z
    .number({ invalid_type_error: campo + ' debe ser un número' })
    .int(campo + ' debe ser un entero de pesos')
    .min(0, campo + ' no puede ser negativo');
export const esquemaEditarParametros = z
    .object({
    ivaPct: esquemaFraccionPct('El IVA').optional(),
    factoringMensualPct: esquemaFraccionPct('El factoring mensual').optional(),
    diasPago: esquemaUmbral('El plazo de pago').optional(),
    margenFletePct: esquemaFraccionPct('El margen de flete').optional(),
    umbralPalletKg: esquemaUmbral('El umbral de pallet').optional(),
    kgPorPallet: esquemaUmbral('Los kilos por pallet').optional(),
    factorInboundConsolidacion: esquemaFactor('El factor de inbound').optional(),
    handlingConsolidacionPorGrupo: esquemaMontoOpcional('El handling por grupo').optional(),
    ticketMinimo: esquemaMontoOpcional('El ticket mínimo').optional(),
    feePedidoPequeno: esquemaMontoOpcional('El fee de pedido pequeño').optional(),
    /*
     * ── Bodega del operador logístico externo ────────────────────────────────
     *
     * `bodegaCiudad` en `null` apaga el paso por bodega: el motor vuelve a
     * elegir el acopio entre las ciudades de los proveedores. Es `nullable` y no
     * solo `optional` justamente para poder apagarla, que es distinto de "no
     * mandar el campo".
     *
     * Las tarifas las pone el OPERADOR, no nosotros. Son los primeros costos del
     * sistema que no controlamos.
     */
    /*
     * LA PERILLA QUE DEFINE EL NEGOCIO: qué oferta ve el cliente cuando varios
     * distribuidores publican la misma parte.
     *
     * La decisión vigente es `mayor_costo`. Está acá y no en el código porque se
     * tomó «por ahora», y medir si conviene moverla requiere poder moverla — sin
     * desplegar y sin pedirle nada a nadie.
     */
    reglaSeleccionOferta: z.enum(REGLAS_SELECCION_DTO).optional(),
    bodegaCiudad: z.enum(CIUDADES_ORIGEN).nullable().optional(),
    bodegaHandlingPorGrupo: esquemaMontoOpcional('El handling de bodega').optional(),
    bodegaInboundLocal: esquemaMontoOpcional('El traslado local a bodega').optional(),
    bodegaObligatoria: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, 'No hay ningún parámetro que cambiar')
    .refine((v) => !(v.bodegaObligatoria === true && v.bodegaCiudad === null), 'No se puede obligar el paso por bodega y apagar la bodega en el mismo cambio.');
// ─── Tarifas de flete ───────────────────────────────────────────────────────
export const esquemaTramoTarifa = z
    .object({
    hastaKg: z
        .number({ invalid_type_error: 'El límite del tramo debe ser un número' })
        .gt(0, 'El límite del tramo debe ser mayor que cero')
        .max(99_999_999, 'El límite del tramo es demasiado grande'),
    precio: z
        .number({ invalid_type_error: 'El precio del tramo debe ser un número' })
        .int('El precio del tramo debe ser un entero de pesos')
        .min(0, 'El precio del tramo no puede ser negativo'),
})
    .strict();
/** Cuántos tramos admite una ruta. Más allá la tabla deja de ser legible. */
export const MAXIMO_TRAMOS_RUTA = 20;
/**
 * Edición de una ruta.
 *
 * `tramos` reemplaza el arreglo COMPLETO, no lo fusiona: una tabla de tramos
 * aplicada a medias cotizaría con una mezcla de la vieja y la nueva, y nadie
 * podría explicar el flete resultante. Enviar el arreglo entero hace que el
 * estado final sea exactamente lo que el operador vio en la pantalla.
 */
export const esquemaEditarTarifa = z
    .object({
    sobreKg: z
        .number({ invalid_type_error: 'El precio por kilo adicional debe ser un número' })
        .int('El precio por kilo adicional debe ser un entero de pesos')
        .min(0, 'El precio por kilo adicional no puede ser negativo')
        .optional(),
    pallet: z
        .number({ invalid_type_error: 'El precio por pallet debe ser un número' })
        .int('El precio por pallet debe ser un entero de pesos')
        .min(0, 'El precio por pallet no puede ser negativo')
        .optional(),
    tramos: z
        .array(esquemaTramoTarifa)
        .min(1, 'Una ruta necesita al menos un tramo')
        .max(MAXIMO_TRAMOS_RUTA, 'La ruta tiene demasiados tramos')
        .optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, 'No hay nada que cambiar en la tarifa');
// ─── Auditoría ──────────────────────────────────────────────────────────────
export const esquemaConsultaAuditoria = esquemaConsultaPagina.extend({
    tabla: z.string().trim().max(60).optional(),
    registroId: z.string().trim().max(60).optional(),
});
// ─── Pago de una orden ──────────────────────────────────────────────────────
/**
 * Sella la fecha de pago de una orden.
 *
 * Existe porque sin ella el DSO no puede existir: en la v1 `pagadaEn` estaba en
 * el esquema y NINGÚN camino de la aplicación la escribía, así que el tablero
 * mostraba el DSO en blanco para siempre y nadie sabía por qué.
 *
 * `pagadaEn: null` deshace la marca —un pago mal imputado se corrige— y el
 * campo ausente significa "ahora".
 */
export const esquemaMarcarPago = z
    .object({
    pagadaEn: z
        .string()
        .datetime({ offset: true, message: 'La fecha de pago debe ser una fecha ISO' })
        .nullish(),
})
    .strict();
// ─── Consultas de KPI ───────────────────────────────────────────────────────
/** `AAAA-MM-DD` o fecha ISO completa. */
const esquemaFechaConsulta = z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}(T.*)?$/, 'La fecha debe tener el formato AAAA-MM-DD');
export const esquemaConsultaKpiOrdenes = esquemaConsultaPagina.extend({
    desde: esquemaFechaConsulta.optional(),
    hasta: esquemaFechaConsulta.optional(),
    clienteId: z.string().trim().length(36, 'El cliente no es válido').optional(),
});
/** Cuántos meses devuelve el resumen mensual por defecto y como tope. */
export const MESES_KPI_DEFECTO = 12;
export const MESES_KPI_MAXIMO = 60;
export const esquemaConsultaKpiMensual = z.object({
    desde: esquemaFechaConsulta.optional(),
    hasta: esquemaFechaConsulta.optional(),
    meses: z.coerce.number().int().min(1).max(MESES_KPI_MAXIMO).default(MESES_KPI_DEFECTO),
    clienteId: z.string().trim().length(36, 'El cliente no es válido').optional(),
});
//# sourceMappingURL=admin.js.map